import React from 'react';
import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';

import Close from '@rover/icons/svg/close.svg';
import { Box, Button, Flex, Paragraph, Text } from '@rover/kibble/core';
import useTheme from '@rover/kibble/themes/useTheme';

import { useImageGallery } from './hooks/useImageGallery';
import { MAX_LIGHTBOX_IMAGE_WIDTH } from './constants';
import type { GalleryItem, PaginationConfig } from './ImageGallery.common';
import { countGalleryVideoItems } from './ImageGallery.common';
import LightboxPager from './LightboxPager';

type LightboxProps = {
  images: GalleryItem[] | undefined;
  lightboxIndex: number;
  setLightboxIndex: React.Dispatch<React.SetStateAction<number>>;
  onClose: () => void;
  onViewGallery?: () => void;
  pagination?: PaginationConfig;
  renderImageFooter?: (index: number) => React.ReactNode;
};

const Lightbox = ({
  images,
  lightboxIndex,
  setLightboxIndex,
  onClose,
  onViewGallery,
  pagination,
  renderImageFooter,
}: LightboxProps): JSX.Element | null => {
  const { colors } = useTheme();

  const videoItemCount = countGalleryVideoItems(images);
  const totalImageCount =
    pagination === undefined ? undefined : pagination.totalImageCount + videoItemCount;

  const gallery = useImageGallery(images, lightboxIndex, { totalImageCount });

  const displayTotal = totalImageCount ?? images?.length ?? 0;

  if (!images) return null;

  const currentImage = images[gallery.currentIndex];

  return (
    <Flex flex="1" alignItems="center" justifyContent="flex-start" flexDirection="column" gap="5x">
      <Flex
        justifyContent={onViewGallery ? 'space-between' : 'flex-end'}
        flexDirection="row"
        width="100%"
        px={['4x', '0x']}
      >
        {onViewGallery && (
          <Button onClick={onViewGallery} contrast>
            {t`View gallery`}
          </Button>
        )}
        <Button
          variant="flat"
          icon={Close}
          onClick={onClose}
          aria-label={t`Dismiss`}
          sx={{
            color: colors.text.primary_inverse,
            '&:hover, &:focus': { color: colors.interactive_link.secondary_inverse_pressed },
          }}
          circular
          contrast
        />
      </Flex>
      <Flex
        flex="1"
        alignItems="center"
        justifyContent="center"
        flexDirection="column"
        width="100%"
      >
        <Text size="100" textColor="primary_inverse" mb="4x">
          <Trans>
            {gallery.currentIndex + 1} of {displayTotal}
          </Trans>
        </Text>
        <LightboxPager
          images={images}
          lightboxIndex={lightboxIndex}
          setLightboxIndex={setLightboxIndex}
          pagination={pagination}
          gallery={gallery}
        />
        {renderImageFooter?.(gallery.currentIndex)}
        <Box aria-hidden>
          {currentImage?.caption && (
            <Paragraph
              size="200"
              mt="10x"
              px="4x"
              textAlign="center"
              textColor="primary_inverse"
              webProps={{ maxWidth: `${MAX_LIGHTBOX_IMAGE_WIDTH}px` }}
            >
              {currentImage.caption}
            </Paragraph>
          )}
        </Box>
        {currentImage?.formattedAddedDate && (
          <Paragraph
            size="100"
            mt={currentImage.caption ? '2x' : '10x'}
            textColor="primary_inverse"
          >
            <Trans>Uploaded {currentImage.formattedAddedDate}</Trans>
          </Paragraph>
        )}
      </Flex>
    </Flex>
  );
};

export default Lightbox;
